import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { UserPermissions } from '../../../api/users';
import type { RootState } from '../../../store';
import type { AuthState, UserCompatibleWithAccountInfo } from './types';

const initialState: AuthState = {
  user: null,
  language: 'en', //[TODO]: has to be moved to a settings slice
  userPermissions: null,
  isLoading: false,
  isUserVerified: true,
  alreadyOpen: false,
  error: null,
  sessionExchangeErrorStatus: null,
  isSwitchingTenantSession: false,
  switchingToTenantName: null,
};

const loginSlice = createSlice({
  name: 'login',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<UserCompatibleWithAccountInfo>) => {
      state.user = { ...state.user, ...action.payload };
    },
    resetUser: state => {
      state.user = null;
      state.userPermissions = null;
    },
    setUserPermissions: (state, action: PayloadAction<UserPermissions>) => {
      state.userPermissions = { ...state.userPermissions, ...action.payload };
    },
    setIsLoginLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setIsUserVerified: (state, action: PayloadAction<boolean>) => {
      state.isUserVerified = action.payload;
    },
    setIsLoginError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setAlertMessage: (state, action: PayloadAction<boolean>) => {
      state.alreadyOpen = action.payload;
    },
    setSessionExchangeErrorStatus: (state, action: PayloadAction<number | null>) => {
      state.sessionExchangeErrorStatus = action.payload;
    },
    setIsSwitchingTenantSession: (
      state,
      action: PayloadAction<{ isSwitching: boolean; tenantName?: string | null }>
    ) => {
      state.isSwitchingTenantSession = action.payload.isSwitching;
      state.switchingToTenantName = action.payload.isSwitching
        ? (action.payload.tenantName ?? null)
        : null;
    },
  },
});

export const {
  setUser,
  setUserPermissions,
  resetUser,
  setIsLoginLoading,
  setIsLoginError,
  setIsUserVerified,
  setAlertMessage,
  setSessionExchangeErrorStatus,
  setIsSwitchingTenantSession,
} = loginSlice.actions;

export const selectUserPermissions = (state: RootState) => state.user.userPermissions;
export const selectUserId = (state: RootState) => state.user.user?.id;
export const selectUser = (state: RootState) => state.user.user;
export const selectLoginError = (state: RootState) => state.user.error;
export const selectLoginSliceIsLoading = (state: RootState) => state.user.isLoading;
export const selectIsUserVerified = (state: RootState) => state.user.isUserVerified;
export const selectAlreadyopen = (state: RootState) => state.user.alreadyOpen;
export const selectSessionExchangeErrorStatus = (state: RootState) =>
  state.user.sessionExchangeErrorStatus;
export const selectIsSwitchingTenantSession = (state: RootState) =>
  state.user.isSwitchingTenantSession;
export const selectSwitchingToTenantName = (state: RootState) => state.user.switchingToTenantName;
export default loginSlice.reducer;
